import { PHASE } from "./constants.ts";
import type { Phase } from "./types.ts";

// Phase-transition cutscenes (plan G1c / §5 moment 10). Four "highlight" phase
// changes surface a full-screen "phase" moment card instead of the legacy info
// toast: 常规赛开幕 / 季后赛 / 选秀 / 自由市场. Every other phase keeps its light
// prompt. The server (worker/core/phase/finalize.ts) decides WHICH phase fired
// and ships the phase number in the event; the UI maps the phase to its title
// copy. CTA url/text come from the phase's own newPhase* redirect where one
// exists; REGULAR_SEASON has no redirect, so a fallback CTA lives here.

export type PhaseMomentSpec = {
	// UI headline (i18n key + English source). Rendered as the overlay title.
	titleKey: string;
	titleText: string;
	// One-line forward-looking outlook, shipped by the worker as the event
	// text/textKey (double-track), translated on the UI via translateEventText.
	outlookKey: string;
	outlookText: string;
	// CTA used only when the phase's newPhase* provides no redirect of its own
	// (REGULAR_SEASON). The other three reuse their existing redirect.
	ctaFallback?: {
		urlParts: (number | string)[];
		text: string;
		textKey: string;
	};
};

export const PHASE_MOMENTS: Partial<Record<Phase, PhaseMomentSpec>> = {
	[PHASE.REGULAR_SEASON]: {
		titleKey: "phaseMoment.titleRegularSeason",
		titleText: "The season tips off!",
		outlookKey: "phaseMoment.outlookRegularSeason",
		outlookText: "The long road to the title starts today.",
		ctaFallback: {
			urlParts: ["daily_schedule"],
			text: "View schedule",
			textKey: "phaseRedirect.viewSchedule",
		},
	},
	[PHASE.PLAYOFFS]: {
		titleKey: "phaseMoment.titlePlayoffs",
		titleText: "The playoffs are here!",
		outlookKey: "phaseMoment.outlookPlayoffs",
		outlookText: "Win four rounds and the trophy is yours.",
	},
	[PHASE.DRAFT]: {
		titleKey: "phaseMoment.titleDraft",
		titleText: "Draft night is here!",
		outlookKey: "phaseMoment.outlookDraft",
		outlookText: "Tonight you add the future of your franchise.",
	},
	[PHASE.FREE_AGENCY]: {
		titleKey: "phaseMoment.titleFreeAgency",
		titleText: "Free agency is open!",
		outlookKey: "phaseMoment.outlookFreeAgency",
		outlookText: "The market is open — go land your targets.",
	},
};

export const getPhaseMomentSpec = (
	phase: Phase,
): PhaseMomentSpec | undefined => PHASE_MOMENTS[phase];

export const isHighlightMomentPhase = (phase: Phase): boolean =>
	Object.hasOwn(PHASE_MOMENTS, phase);

// The phase card auto-advances to its CTA target only when the user keeps the
// phaseChangeRedirects setting on for this phase (mirrors the legacy auto-jump).
// Off → the card waits for the player to close it or tap the CTA.
export const phaseMomentAutoAdvances = (
	phaseChangeRedirects: Phase[],
	phase: Phase,
): boolean => phaseChangeRedirects.includes(phase);

// Phase cutscenes must never lock the sim (plan G1c): they are a passing beat,
// not a demand for attention. Celebration/somber moments still stop the sim on
// show (mirroring persistent toasts).
export const momentVariantStopsSim = (variant: string): boolean =>
	variant !== "phase";

// Delay before a phase card auto-advances to its CTA target.
export const PHASE_MOMENT_AUTO_ADVANCE_MS = 2000;
